import React from 'react';
import { AlertTriangle, CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button, Input, Label } from 'shared-portal-components-v2';

import PasswordRequirements from '@/components/molecules/PasswordRequirements/PasswordRequirements';
import SetPasswordError from '@/components/molecules/SetPasswordError/SetPasswordError';
import type {
  ChangePasswordPhase,
  ChangePasswordStage,
} from '@/store/slices/changePassword/changePasswordSlice';

export interface ChangePasswordViewProps {
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  passwordError: string | undefined;
  globalError: string | undefined;
  stage: ChangePasswordStage | undefined;
  phase: ChangePasswordPhase;
  isRunning: boolean;
  isComplete: boolean;
  isAwaitingConfirmation: boolean;
  isLeaveBlocked: boolean;
  isSubmitDisabled: boolean;
  regeneratedCount: number;
  totalCount: number;
  failedCount: number;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onTogglePassword: () => void;
  onToggleConfirmPassword: () => void;
  onSubmit: () => void;
  onConfirmProceed: () => void;
  onCancelConfirmation: () => void;
  onConfirmLeave: () => void;
  onStayHere: () => void;
}

const PHASE_LABEL: Record<ChangePasswordPhase, string> = {
  idle: 'Ready',
  preparing: 'Preparing your new keys...',
  regenerating: 'Regenerating file keys...',
  awaitingConfirmation: 'Waiting for your confirmation...',
  bundling: 'Bundling file keys...',
  uploading: 'Uploading keys to the secure network...',
  submitting: 'Transferring your keys to the network...',
  committing: 'Setting your new password...',
  complete: 'Password changed',
  error: 'Something went wrong',
};

interface PasswordInputFieldProps {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  isShown: boolean;
  isDisabled: boolean;
  error?: string;
  onChange: (value: string) => void;
  onToggle: () => void;
}

function PasswordInputField(props: PasswordInputFieldProps): React.JSX.Element {
  const { id, label, placeholder, value, isShown, isDisabled, error, onChange, onToggle } = props;
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id} isRequired className="text-base">
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          type={isShown ? 'text' : 'password'}
          required
          placeholder={placeholder}
          value={value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onChange(event.target.value);
          }}
          variant="outline"
          inputClassName="h-12 text-base sm:text-sm pr-12"
          error={error}
          disabled={isDisabled}
        />
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          className="absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:text-foreground bg-transparent shadow-none"
          onClick={onToggle}
          disabled={isDisabled}
        >
          {isShown ? (
            <EyeOff className="h-4 w-4" aria-hidden />
          ) : (
            <Eye className="h-4 w-4" aria-hidden />
          )}
          <span className="sr-only">{isShown ? 'Hide password' : 'Show password'}</span>
        </Button>
      </div>
    </div>
  );
}

interface LeaveInProgressPanelProps {
  onStayHere: () => void;
  onConfirmLeave: () => void;
}

function LeaveInProgressPanel({ onStayHere, onConfirmLeave }: LeaveInProgressPanelProps) {
  return (
    <div
      role="alertdialog"
      aria-label="A password change is in progress"
      className="flex items-start gap-3 rounded-md border-2 border-destructive bg-destructive/10 p-4 text-sm text-destructive"
    >
      <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <div className="flex flex-col gap-3">
        <p className="font-semibold">A password change is still in progress</p>
        <p>
          If you leave now your password will not be reset.
        </p>
        <div className="flex items-center gap-3">
          <Button type="button" variant="secondary" size="sm" onClick={onStayHere}>
            Stay on this page
          </Button>
          <Button type="button" variant="destructive" size="sm" onClick={onConfirmLeave}>
            Leave anyway
          </Button>
        </div>
      </div>
    </div>
  );
}

interface RegenerationFailurePanelProps {
  failedCount: number;
  totalCount: number;
  onCancelConfirmation: () => void;
  onConfirmProceed: () => void;
}

function RegenerationFailurePanel(props: RegenerationFailurePanelProps) {
  const { failedCount, totalCount, onCancelConfirmation, onConfirmProceed } = props;
  return (
    <div
      role="alertdialog"
      aria-label="Some file keys could not be regenerated"
      className="flex items-start gap-3 rounded-md border-2 border-destructive bg-destructive/10 p-4 text-sm text-destructive"
    >
      <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <div className="flex flex-col gap-3">
        <p className="font-semibold">Some file keys could not be regenerated</p>
        <p>
          {failedCount} of {totalCount} file key{totalCount === 1 ? '' : 's'} could not be
          regenerated. If you continue, you will permanently lose access to the files those keys
          protect. Your password has not been changed yet.
        </p>
        <div className="flex items-center gap-3">
          <Button type="button" variant="secondary" size="sm" onClick={onCancelConfirmation}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" size="sm" onClick={onConfirmProceed}>
            Continue and lose access
          </Button>
        </div>
      </div>
    </div>
  );
}

interface RunningProgressPanelProps {
  phase: ChangePasswordPhase;
  regeneratedCount: number;
  totalCount: number;
}

function RunningProgressPanel({ phase, regeneratedCount, totalCount }: RunningProgressPanelProps) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span>{PHASE_LABEL[phase]}</span>
      </div>
      {phase === 'regenerating' && (
        <p className="text-sm text-muted-foreground">
          Regenerated {regeneratedCount} of {totalCount} file keys
        </p>
      )}
    </div>
  );
}

interface CompletionPanelProps {
  regeneratedCount: number;
  failedCount: number;
}

function CompletionPanel({ regeneratedCount, failedCount }: CompletionPanelProps) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4">
      <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
        <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
        <span className="font-semibold">Your password has been changed.</span>
      </div>
      <p className="text-sm text-muted-foreground">
        {regeneratedCount} file key{regeneratedCount === 1 ? '' : 's'} regenerated
        {failedCount > 0 ? `, ${String(failedCount)} could not be regenerated` : ''}.
      </p>
    </div>
  );
}

export function ChangePasswordView(props: ChangePasswordViewProps): React.JSX.Element {
  const {
    password,
    confirmPassword,
    showPassword,
    showConfirmPassword,
    passwordError,
    globalError,
    stage,
    phase,
    isRunning,
    isComplete,
    isAwaitingConfirmation,
    isLeaveBlocked,
    isSubmitDisabled,
    regeneratedCount,
    totalCount,
    failedCount,
    onPasswordChange,
    onConfirmPasswordChange,
    onTogglePassword,
    onToggleConfirmPassword,
    onSubmit,
    onConfirmProceed,
    onCancelConfirmation,
    onConfirmLeave,
    onStayHere,
  } = props;

  return (
    <div className="w-full max-w-md flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold text-foreground">Change Password</h1>
        <p className="text-muted-foreground text-base">
          Choose a new password. We&apos;ll re-encrypt your file keys so you keep access to your
          files.
        </p>
      </div>

      {isRunning && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-md border-2 border-amber-500/60 bg-amber-500/10 p-4 text-sm text-amber-700 dark:text-amber-400"
        >
          <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <p className="font-medium">
            Do not navigate away or close this tab. If you leave now, your new password will not be
            set.
          </p>
        </div>
      )}

      <SetPasswordError stage={stage} message={globalError} mode="change" />

      {isLeaveBlocked && (
        <LeaveInProgressPanel onStayHere={onStayHere} onConfirmLeave={onConfirmLeave} />
      )}

      {isAwaitingConfirmation && (
        <RegenerationFailurePanel
          failedCount={failedCount}
          totalCount={totalCount}
          onCancelConfirmation={onCancelConfirmation}
          onConfirmProceed={onConfirmProceed}
        />
      )}

      {!isComplete && !isAwaitingConfirmation && (
        <form
          className="flex flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <div className="flex flex-col gap-4">
            <PasswordInputField
              id="new-password"
              label="New Password"
              placeholder="Enter new password"
              value={password}
              isShown={showPassword}
              isDisabled={isRunning}
              error={passwordError}
              onChange={onPasswordChange}
              onToggle={onTogglePassword}
            />
            <PasswordInputField
              id="confirm-new-password"
              label="Confirm Password"
              placeholder="Re-enter new password"
              value={confirmPassword}
              isShown={showConfirmPassword}
              isDisabled={isRunning}
              onChange={onConfirmPasswordChange}
              onToggle={onToggleConfirmPassword}
            />
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <PasswordRequirements
              password={password}
              confirmPassword={confirmPassword}
              includeMatchRequirement
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="md"
            className="h-12 text-base font-semibold"
            isLoading={isRunning}
            loadingText={PHASE_LABEL[phase]}
            disabled={isSubmitDisabled}
          >
            Change Password
          </Button>
        </form>
      )}

      {isRunning && (
        <RunningProgressPanel
          phase={phase}
          regeneratedCount={regeneratedCount}
          totalCount={totalCount}
        />
      )}

      {isComplete && (
        <CompletionPanel regeneratedCount={regeneratedCount} failedCount={failedCount} />
      )}
    </div>
  );
}

export default ChangePasswordView;
